<template>
  <v-form class="sales-excel-upload-analysis">
    <v-container>
      <div style="height: 10px;"></div>

      <v-layout row wrap>
        <v-flex xs12>
          <h2 class="headline mb-3">販売実績Excel取込確認</h2>
        </v-flex>
      </v-layout>

      <v-card class="mb-4">
        <v-card-title>
          <span class="title">Excel分析</span>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 sm4>
              <v-text-field
                label="取引先コード"
                v-model="partnerCode"
                :class="{ 'problem-field': hasProblem('partnerCode') }"
                :error-messages="problemMessages('partnerCode')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm4>
              <v-text-field
                label="対象月"
                hint="yyyyMM"
                persistent-hint
                v-model="targetMonth"
                :class="{ 'problem-field': hasProblem('targetMonth') }"
                :error-messages="problemMessages('targetMonth')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm4>
              <v-text-field
                label="固定店舗コード"
                hint="本文に店舗コードがない場合のみ使用"
                persistent-hint
                readonly
                disabled
                v-model="fixedOfficialStoreCode"
                :class="{ 'problem-field': hasProblem('fixedOfficialStoreCode') }"
                :error-messages="problemMessages('fixedOfficialStoreCode')"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap class="action-button-row">            
            <v-flex xs12 sm4 class="action-button-col">
              <v-btn block color="primary" dark :disabled="!canConfirm" :loading="confirming" @click="confirmExcel()">
                <v-icon dark left>check</v-icon>確認して保存
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 class="action-button-col">
              <v-btn block color="#4C728A" dark :to="{ name: 'admin/new-admin-menu' }">
                <v-icon color="black" left>home</v-icon>Home
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <v-alert v-if="noticeMessage" :value="true" type="info" class="mb-4">
        {{ noticeMessage }}
      </v-alert>

      <v-alert v-if="errorItems.length > 0" :value="true" :type="hasBlockingError ? 'error' : 'warning'" class="mb-4">
        <div class="subheading mb-2">処理メッセージ</div>
        <div v-for="(error, index) in errorItems" :key="'error-' + index" class="mb-1">
          <strong>{{ error.errorCode || error.error || 'UNKNOWN' }}</strong>
          <span v-if="error.sheetName"> / {{ error.sheetName }}</span>
          <span v-if="error.cellAddress"> / {{ error.cellAddress }}</span>
          <span> : {{ error.message || error.errorDescription || error.action || error.rawValue }}</span>
        </div>
      </v-alert>

      <v-card v-if="rules.length > 0" class="mb-4">
        <v-card-title>
          <span class="title">AI分析結果</span>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 sm4>
              <v-select
                label="確認対象シート"
                :items="ruleItems"
                item-text="text"
                item-value="value"
                v-model="activeRuleIndex"
                :class="{ 'problem-field': hasProblem('sheetName') }"
                :error-messages="problemMessages('sheetName')"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4>
              <v-select
                label="レイアウト"
                :items="layoutTypes"
                v-model="activeRule.layoutType"
                :class="{ 'problem-field': hasProblem('layoutType') }"
                :error-messages="problemMessages('layoutType')"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4>
              <v-text-field
                label="信頼度"
                readonly
                :value="activeRule.confidence"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 sm3>
              <v-text-field
                label="シート名"
                v-model="activeRule.sheetName"
                :class="{ 'problem-field': hasProblem('sheetName') }"
                :error-messages="problemMessages('sheetName')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="開始行"
                type="number"
                v-model.number="rowStart"
                :class="{ 'problem-field': hasProblem('rowRange') }"
                :error-messages="problemMessages('rowRange')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="終了行"
                type="number"
                v-model.number="rowEnd"
                :class="{ 'problem-field': hasProblem('rowRange') }"
                :error-messages="problemMessages('rowRange')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="固定店舗コード"
                readonly
                :value="fixedStoreCodeRequiredText"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 sm3>
              <v-text-field
                label="JAN列"
                v-model="activeRule.janCol"
                :class="{ 'problem-field': hasProblem('janCol') }"
                :error-messages="problemMessages('janCol')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="商品名列"
                v-model="activeRule.itemNameCol"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="取引先店舗コード列"
                v-model="activeRule.partnerStoreCodeCol"
                :class="{ 'problem-field': hasProblem('partnerStoreCodeCol') }"
                :error-messages="problemMessages('partnerStoreCodeCol')"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                label="数量列"
                v-model="activeRule.quantityCol"
                :disabled="activeRule.layoutType !== 'VERTICAL'"
                :class="{ 'problem-field': hasProblem('quantityCol') }"
                :error-messages="problemMessages('quantityCol')"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap v-if="activeRule.layoutType === 'VERTICAL'">
            <v-flex xs12 sm4>
              <v-text-field
                label="販売日列"
                v-model="activeRule.saleDateCol"
                :class="{ 'problem-field': hasProblem('saleDateCol') }"
                :error-messages="problemMessages('saleDateCol')"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap v-if="activeRule.layoutType === 'HORIZONTAL'">
            <v-flex xs12>
              <div class="subheading mb-2">日付別数量列</div>
              <v-data-table
                :headers="dateColumnHeaders"
                :items="activeRule.dateQuantityColumns"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr :class="{ 'problem-row': hasProblem('dateQuantityColumns') }">
                    <td>
                      <v-text-field
                        label="販売日"
                        v-model="props.item.saleDate"
                        hide-details
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        label="数量列"
                        v-model="props.item.quantityCol"
                        hide-details
                      ></v-text-field>
                    </td>
                    <td class="text-xs-center">
                      <v-btn icon small color="error" @click="removeDateQuantityColumn(props.index)">
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </v-data-table>
              <v-btn small color="primary" dark class="mt-2" @click="addDateQuantityColumn()">
                <v-icon small left>add</v-icon>日付列追加
              </v-btn>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 sm4>
              <v-textarea
                label="除外行"
                hint="JSON形式"
                persistent-hint
                rows="5"
                v-model="activeRule.excludeRowsText"
                :class="{ 'problem-field': hasProblem('exclude') }"
                :error-messages="problemMessages('exclude')"
              ></v-textarea>
            </v-flex>
            <v-flex xs12 sm4>
              <v-textarea
                label="除外列"
                hint="JSON形式"
                persistent-hint
                rows="5"
                v-model="activeRule.excludeColumnsText"
              ></v-textarea>
            </v-flex>
            <v-flex xs12 sm4>
              <v-textarea
                label="除外ブロック"
                hint="JSON形式"
                persistent-hint
                rows="5"
                v-model="activeRule.excludeBlocksText"
              ></v-textarea>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12>
              <v-textarea
                label="AI判断理由"
                rows="3"
                readonly
                :value="displayReason"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

    </v-container>
  </v-form>
</template>

<script>
export default {
  created() {
    const result = this.$route.params.analyzeResult

    if (!result) {
      this.$parent.$parent.$parent.showErrorDialog(
        'アップロード結果がありません'
      )

      this.$router.push({
        name: 'admin/sales-excel-upload'
      })
      return
    }    

    this.partnerCode = result.partnerCode || ''
    this.targetMonth = result.targetMonth || ''
    this.fixedOfficialStoreCode = result.fixedOfficialStoreCode || ''

    this.applyAnalyzeResult(result)
  },
  data() {
    return {
      partnerCode: '',
      targetMonth: '',
      fixedOfficialStoreCode: '',

      uploadId: '',
      rules: [],
      activeRuleIndex: 0,
      errorItems: [],
      rawResponseText: '',
      noticeMessage: '',
      confirming: false,
      confirmed: false,

      layoutTypes: ['VERTICAL', 'HORIZONTAL'],
      dateColumnHeaders: [
        { text: '販売日', value: 'saleDate', sortable: false },
        { text: '数量列', value: 'quantityCol', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    canConfirm() {
      return this.uploadId !== '' && this.rules.length > 0 && !this.confirming && !this.confirmed && !this.hasBlockingError
    },
    hasBlockingError() {
      return this.errorItems.some(error => {
        const errorCode = error.errorCode || error.error || ''
        return errorCode === 'AI_ANALYSIS_FAILED' || errorCode === 'AI_RULE_JSON_PARSE_FAILED'
      })
    },
    activeRule() {
      return this.rules[this.activeRuleIndex] || {}
    },
    ruleItems() {
      return this.rules.map((rule, index) => {
        return {
          value: index,
          text: (rule.sheetName || 'sheet' + (index + 1)) + ' / ' + (rule.layoutType || 'UNKNOWN')
        }
      })
    },
    rowStart: {
      get() {
        if (this.activeRule.layoutType === 'HORIZONTAL') {
          return this.activeRule.productRowStart
        }
        return this.activeRule.dataRowStart
      },
      set(value) {
        if (this.activeRule.layoutType === 'HORIZONTAL') {
          this.$set(this.activeRule, 'productRowStart', value)
        } else {
          this.$set(this.activeRule, 'dataRowStart', value)
        }
      }
    },
    rowEnd: {
      get() {
        if (this.activeRule.layoutType === 'HORIZONTAL') {
          return this.activeRule.productRowEnd
        }
        return this.activeRule.dataRowEnd
      },
      set(value) {
        if (this.activeRule.layoutType === 'HORIZONTAL') {
          this.$set(this.activeRule, 'productRowEnd', value)
        } else {
          this.$set(this.activeRule, 'dataRowEnd', value)
        }
      }
    },
    fixedStoreCodeRequiredText() {
      return this.activeRule.fixedOfficialStoreCodeRequired ? '必要' : '不要'
    },
    displayReason() {
      if (!this.activeRule.reason) {
        return ''
      }
      return this.hasJapaneseText(this.activeRule.reason)
        ? this.activeRule.reason
        : 'AI判断理由が英語で返されたため表示していません。再分析すると日本語で表示されます。'
    }
  },
  methods: {
    confirmExcel() {
      if (!this.canConfirm) {
        return
      }

      let formData = new FormData()
      formData.append('partnerCode', this.partnerCode)
      formData.append('targetMonth', this.targetMonth)
      formData.append('uploadId', this.uploadId)
      formData.append('confirmedRule', JSON.stringify({
        fixedOfficialStoreCode: this.fixedOfficialStoreCode,
        rules: this.buildConfirmRules()
      }))

      this.confirming = true
      this.noticeMessage = ''

      ServerApiCallService.confirmSalesExcel(formData)
      .then((response) => {
        const body = response.data

        this.rawResponseText = JSON.stringify(body, null, 2)
        this.errorItems = this.normalizeErrors(body)

        this.noticeMessage = this.errorItems.length > 0
          ? '保存前の検証で確認が必要な項目があります。'
          : '販売実績を保存しました。'
        this.confirmed = this.errorItems.length === 0
      })
      .catch((error) => {
        console.log('There was an error:', error)

        this.errorItems = [{
          errorCode: 'CONFIRM_REQUEST_FAILED',
          message: error.response && error.response.data
            ? (error.response.data.message || error.response.data.errorDescription || '保存処理に失敗しました。')
            : '保存処理に失敗しました。'
        }]
      })
      .finally(() => {
        this.confirming = false
      })    
    },
    applyAnalyzeResult(body) {
      this.rawResponseText = JSON.stringify(body, null, 2)
      this.uploadId = body.uploadId || ''
      this.errorItems = this.normalizeErrors(body)
      this.noticeMessage = this.hasBlockingError
        ? 'AI分析に失敗したため保存できません。ファイルを再度アップロードしてください。'
        : (this.errorItems.length > 0 ? '処理メッセージを確認し、必要な項目を修正してから保存してください。' : '')

      let aiRule = body.aiRuleJson || body.aiRule || body.rule || body
      if (typeof aiRule === 'string') {
        try {
          aiRule = JSON.parse(aiRule)
        } catch (e) {
          this.errorItems = this.uniqueErrors(this.errorItems.concat([{
            errorCode: 'AI_RULE_JSON_PARSE_FAILED',
            message: 'AI分析結果を読み取れないため保存できません。ファイルを再度アップロードしてください。'
          }]))
          this.noticeMessage = 'AI分析結果を読み取れないため保存できません。ファイルを再度アップロードしてください。'
          aiRule = {}
        }
      }
      this.rules = this.normalizeRules(aiRule).map(rule => this.decorateRule(rule))
      this.activeRuleIndex = 0
    },
    normalizeRules(aiRule) {
      if (Array.isArray(aiRule)) {
        return aiRule
      }
      if (aiRule && Array.isArray(aiRule.rules)) {
        return aiRule.rules
      }
      if (aiRule && Array.isArray(aiRule.sheets)) {
        return aiRule.sheets
      }
      if (aiRule && aiRule.sheetName) {
        return [aiRule]
      }
      return []
    },
    normalizeErrors(body) {
      let errors = []
      if (Array.isArray(body.errors)) {
        errors = body.errors
      } else if (body.errorSummary && Array.isArray(body.errorSummary.errors)) {
        errors = body.errorSummary.errors
      } else if (Array.isArray(body.errorSummary)) {
        errors = body.errorSummary
      } else if (body.errorCode || body.error || body.errorDescription) {
        errors = [body]
      }
      return this.uniqueErrors(errors)
    },
    uniqueErrors(errors) {
      let seen = {}
      return errors.filter(error => {
        let key = [
          error.errorCode || error.error || '',
          error.message || error.errorDescription || '',
          error.sheetName || '',
          error.cellAddress || '',
          error.rawValue || ''
        ].join('|')
        if (seen[key]) {
          return false
        }
        seen[key] = true
        return true
      })
    },
    decorateRule(rule) {
      let copied = Object.assign({
        sheetName: '',
        layoutType: 'VERTICAL',
        janCol: '',
        itemNameCol: '',
        partnerStoreCodeCol: '',
        saleDateCol: '',
        quantityCol: '',
        dateQuantityColumns: [],
        fixedOfficialStoreCodeRequired: false,
        confidence: '',
        reason: ''
      }, rule)

      copied.dateQuantityColumns = Array.isArray(copied.dateQuantityColumns)
        ? copied.dateQuantityColumns
        : []
      copied.excludeRowsText = this.stringifyJson(copied.excludeRows || [])
      copied.excludeColumnsText = this.stringifyJson(copied.excludeColumns || [])
      copied.excludeBlocksText = this.stringifyJson(copied.excludeBlocks || [])
      return copied
    },
    buildConfirmRules() {
      return this.rules.map(rule => {
        let copied = Object.assign({}, rule)
        copied.excludeRows = this.parseJsonText(copied.excludeRowsText, [])
        copied.excludeColumns = this.parseJsonText(copied.excludeColumnsText, [])
        copied.excludeBlocks = this.parseJsonText(copied.excludeBlocksText, [])
        delete copied.excludeRowsText
        delete copied.excludeColumnsText
        delete copied.excludeBlocksText
        return copied
      })
    },
    stringifyJson(value) {
      return JSON.stringify(value, null, 2)
    },
    parseJsonText(value, fallback) {
      if (!value) {
        return fallback
      }
      try {
        return JSON.parse(value)
      } catch (e) {
        return fallback
      }
    },
    addDateQuantityColumn() {
      if (!Array.isArray(this.activeRule.dateQuantityColumns)) {
        this.$set(this.activeRule, 'dateQuantityColumns', [])
      }
      this.activeRule.dateQuantityColumns.push({
        saleDate: '',
        quantityCol: ''
      })
    },
    removeDateQuantityColumn(index) {
      this.activeRule.dateQuantityColumns.splice(index, 1)
    },
    hasProblem(field) {
      return this.problemMessages(field).length > 0
    },
    problemMessages(field) {
      let tokens = this.problemTokens(field)
      if (tokens.length === 0 || this.errorItems.length === 0) {
        return []
      }
      let messages = this.errorItems
        .filter(error => {
          let text = JSON.stringify(error).toLowerCase()
          return tokens.some(token => text.indexOf(token.toLowerCase()) >= 0)
        })
        .map(error => error.message || error.errorDescription || error.errorCode || error.error)
      return messages.filter((message, index, array) => array.indexOf(message) === index)
    },
    problemTokens(field) {
      let tokenMap = {
        partnerCode: ['partnerCode', 'PARTNER_CODE'],
        targetMonth: ['targetMonth', 'TARGET_MONTH', 'SALE_DATE_OUT_OF_TARGET_MONTH'],
        fixedOfficialStoreCode: ['fixedOfficialStoreCode', 'FIXED_OFFICIAL_STORE_CODE'],
        sheetName: ['sheetName', 'DATA_SHEET', 'TARGET_SHEET', 'INVALID_TARGET_SHEET'],
        layoutType: ['layoutType', 'AI_RULE_INVALID_LAYOUT'],
        rowRange: ['ROW_RANGE', 'dataRowStart', 'dataRowEnd', 'productRowStart', 'productRowEnd'],
        janCol: ['jan', 'JAN_EMPTY', 'JAN_NORMALIZE_FAILED', 'ITEM_NOT_FOUND'],
        saleDateCol: ['saleDate', 'SALE_DATE'],
        quantityCol: ['quantity', 'QUANTITY'],
        dateQuantityColumns: ['dateQuantityColumns', 'quantityCol', 'saleDate', 'QUANTITY', 'SALE_DATE'],
        partnerStoreCodeCol: ['partnerStoreCode', 'STORE_MAPPING'],
        exclude: ['exclude', 'SUMMARY_ROW_INCLUDED', 'INVALID_EXCLUDE_RULE']
      }
      return tokenMap[field] || []
    },
    hasJapaneseText(value) {
      return /[\u3040-\u30ff\u3400-\u9fff]/.test(value)
    }
  }
}
</script>

<style>
.sales-excel-upload-analysis .problem-field .v-input__slot {
  border-left: 4px solid #e53935;
  background-color: #fff5f5;
}

.sales-excel-upload-analysis .problem-row {
  background-color: #fff5f5;
}

.sales-excel-upload-analysis .action-button-row {
  margin-top: 8px;
}

.sales-excel-upload-analysis .action-button-col {
  padding-right: 12px;
  padding-bottom: 8px;
}
</style>
